import platform from "platform";

import { PongData } from "~/features/andon/andon.types";

export async function collectPongData({
  extendPongData
}: {
  extendPongData: boolean;
}): Promise<PongData> {
  // ------------------------------------------------------------------
  // NETWORK (Chromium-only)
  // ------------------------------------------------------------------
  let network: PongData["network"] | undefined;

  const conn =
    typeof navigator !== "undefined" && "connection" in navigator
      ? (navigator.connection as Partial<NetworkInformation>)
      : undefined;

  if (conn && (conn.effectiveType || conn.rtt || conn.downlink)) {
    network = {
      effectiveType: conn.effectiveType ?? undefined,
      rtt: conn.rtt ?? undefined,
      downlink: conn.downlink ?? undefined
    };
  }

  // ------------------------------------------------------------------
  // CPU (Universal)
  // ------------------------------------------------------------------
  const cpu: PongData["cpu"] | undefined =
    typeof navigator.hardwareConcurrency === "number"
      ? { cores: navigator.hardwareConcurrency }
      : undefined;

  // ------------------------------------------------------------------
  // User Agent (Universal)
  // ------------------------------------------------------------------
  const userAgent = navigator.userAgent;
  const parsed = platform.parse(userAgent);

  let osName = "Unknown";
  let osVersion = "";

  if (typeof parsed.os === "string") {
    osName = parsed.os;
  } else if (parsed.os && typeof parsed.os === "object") {
    osName = parsed.os.family ?? "Unknown";
    osVersion = parsed.os.version ?? "";
  }

  const userAgentParsed: PongData["userAgentParsed"] = {
    browser: parsed.name ?? "Unknown",
    version: parsed.version ?? "?",
    os: `${osName} ${osVersion}`.trim(),
    platform: parsed.product ?? "desktop",
    isMobile: ["iPhone", "iPad", "iPod", "Android"].includes(
      parsed.product ?? ""
    )
  };

  // ------------------------------------------------------------------
  // User-Agent Client Hints (Chromium-only)
  // ------------------------------------------------------------------
  let userAgentLowEntropy: PongData["userAgentLowEntropy"] | undefined;
  let userAgentHighEntropy: PongData["userAgentHighEntropy"] | undefined;

  if ("userAgentData" in navigator && navigator.userAgentData) {
    const ua = navigator.userAgentData;

    userAgentLowEntropy = {
      brands: ua.brands,
      mobile: ua.mobile,
      platform: ua.platform
    };

    if (ua.getHighEntropyValues) {
      const high = await ua.getHighEntropyValues([
        "architecture",
        "bitness",
        "model",
        "platform",
        "platformVersion",
        "uaFullVersion",
        "fullVersionList"
      ]);

      userAgentHighEntropy = {
        architecture: high.architecture,
        bitness: high.bitness,
        model: high.model,
        platform: high.platform,
        platformVersion: high.platformVersion,
        uaFullVersion: high.uaFullVersion,
        fullVersionList: high.fullVersionList
      };
    }
  }

  // ------------------------------------------------------------------
  // MEMORY (Chromium-only) (Extended PongData)
  // ------------------------------------------------------------------
  let memory: PongData["memory"] | undefined;
  if (extendPongData && "memory" in performance) {
    const perfWithMemory = performance as Performance & {
      memory: {
        usedJSHeapSize: number;
        totalJSHeapSize: number;
        jsHeapSizeLimit: number;
      };
    };
    memory = {
      usedJSHeapSize: perfWithMemory.memory.usedJSHeapSize,
      totalJSHeapSize: perfWithMemory.memory.totalJSHeapSize,
      jsHeapSizeLimit: perfWithMemory.memory.jsHeapSizeLimit
    };
  }

  // ------------------------------------------------------------------
  // FPS (Universal) (Extended PongData)
  // ------------------------------------------------------------------
  const fps = extendPongData ? await measureFPS(20) : undefined;

  return {
    userAgent,
    userAgentParsed,
    cpu,
    fps,
    network,
    memory,
    userAgentLowEntropy,
    userAgentHighEntropy
  };
}

/**
 * requestAnimationFrame is a browser API that schedules a function to execute before the next screen update.
 * Here, it's used to accurately measure frame rate by timing how many frames can be processed within a
 * given interval. This measurement helps understand the rendering performance of the browser under current conditions.
 */
function measureFPS(numOfFramesToCount: number): Promise<number> {
  return new Promise((resolve) => {
    let framesCounted = 0;
    const start = performance.now();

    function handleNextFrame() {
      framesCounted++;
      if (framesCounted < numOfFramesToCount) {
        requestAnimationFrame(handleNextFrame);
      } else {
        const duration = performance.now() - start;
        const fps = (framesCounted * 1000) / duration;
        const roundedFPS = Math.round(fps * 100) / 100;
        resolve(roundedFPS);
      }
    }

    requestAnimationFrame(handleNextFrame);
  });
}
